/**
 * A shared communication's record (`customer_communications.communication`, Ben K6) read for its record section: the
 * text, the customers and people it concerns, and the link to the original. Headless; the web shell draws it.
 */
import type { RecordView } from "./object-ui/contract";

export interface SharedCommunicationCustomer {
  customer_code: string;
  name: string;
}

export interface SharedCommunicationPerson {
  person_id: string;
  name: string;
  email: string;
}

export interface SharedCommunication {
  kindLabel: string;
  text: string;
  sourceUrl: string | null;
  customers: SharedCommunicationCustomer[];
  people: SharedCommunicationPerson[];
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function rowsOf<T>(value: unknown, keys: readonly (keyof T & string)[]): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (row): row is T =>
      isObject(row) && keys.every((key) => typeof row[key] === "string"),
  );
}

/** The communication on a record view, or null for a record that is not one (or arrived malformed). */
export function sharedCommunicationOf(
  record: RecordView,
): SharedCommunication | null {
  const view = record as unknown as Record<string, unknown>;
  if (view.type !== "communication" || typeof view.text !== "string")
    return null;
  const url = view.source_url;
  return {
    kindLabel:
      typeof view.kind_label === "string" ? view.kind_label : "Communication",
    text: view.text,
    // Only an http(s) link is drawn as one.
    sourceUrl:
      typeof url === "string" && /^https?:\/\//i.test(url) ? url : null,
    customers: rowsOf<SharedCommunicationCustomer>(view.customers, [
      "customer_code",
      "name",
    ]),
    people: rowsOf<SharedCommunicationPerson>(view.people, [
      "person_id",
      "name",
      "email",
    ]),
  };
}
